import React, { useMemo } from 'react';
import { FUENTE, useTema } from './tema';

/**
 * Bienvenida — la pantalla de entrada, antes de iniciar sesión.
 *
 * Es lo único que ve quien todavía no tiene sesión, así que su trabajo es
 * explicar en dos líneas qué hace esto y dejar un solo camino claro: el botón.
 *
 * La maqueta de la derecha es decorativa —va con `aria-hidden`— y enseña las
 * herramientas que la app tiene de verdad. Poner ahí funciones que no existen
 * sería prometer lo que no hay.
 *
 * Sigue el tema como el resto de la v2. Aquí todavía no se sabe quién entra, de
 * modo que la preferencia que se aplica es la del equipo (`sistema`) o la que
 * quedara guardada en este navegador.
 */

const HERRAMIENTAS = [
  { icono: '🎥', nombre: 'Reuniones', color: '#2563eb' },
  { icono: '📝', nombre: 'Transcripción', color: '#7c3aed' },
  { icono: '📄', nombre: 'Minutas', color: '#059669' },
  { icono: '📐', nombre: 'Formatos Word', color: '#dc2626' },
  { icono: '📅', nombre: 'Calendario', color: '#d97706' },
  { icono: '✨', nombre: 'Informe IA', color: '#0891b2' }
];

const estilos = (C) => {
  const claro = C.modo === 'claro';

  return {
    raiz: {
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: FUENTE,
      color: C.texto,
      background: claro
        ? 'linear-gradient(135deg, #f5f7ff 0%, #eef2fb 45%, #e8edfa 100%)'
        : 'linear-gradient(135deg, #0b1220 0%, #0f172a 45%, #141c31 100%)'
    },

    cuerpo: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      gap: 64,
      maxWidth: 1360,
      width: '100%',
      margin: '0 auto',
      padding: '56px 48px',
      boxSizing: 'border-box'
    },

    izquierda: { flex: '1 1 460px', minWidth: 0, maxWidth: 620 },

    marca: { display: 'flex', alignItems: 'center', gap: 14, marginBottom: 56 },
    marcaIcono: {
      width: 52, height: 52, borderRadius: 14, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(140deg, #6366f1 0%, #4f46e5 100%)',
      boxShadow: '0 8px 20px rgba(79, 70, 229, .28)'
    },
    marcaNombre: { fontSize: 16.5, fontWeight: 700, lineHeight: 1.25 },
    marcaSub: { fontSize: 14, color: C.suave, lineHeight: 1.25 },

    titulo: {
      fontSize: 'clamp(36px, 5vw, 58px)',
      fontWeight: 800,
      lineHeight: 1.08,
      letterSpacing: '-0.025em',
      margin: '0 0 22px'
    },
    acento: {
      background: 'linear-gradient(95deg, #6366f1 0%, #8b5cf6 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },

    entradilla: {
      fontSize: 17.5,
      lineHeight: 1.6,
      color: C.suave,
      margin: '0 0 38px',
      maxWidth: 480
    },

    boton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 14,
      padding: '17px 28px',
      borderRadius: 12,
      border: 'none',
      cursor: 'pointer',
      fontSize: 16,
      fontWeight: 600,
      fontFamily: FUENTE,
      background: claro ? '#0f172a' : '#f1f5f9',
      color: claro ? '#ffffff' : '#0f172a',
      boxShadow: claro
        ? '0 10px 26px rgba(15, 23, 42, .22)'
        : '0 10px 26px rgba(0, 0, 0, .45)'
    },

    // ── Maqueta decorativa ──
    derecha: {
      flex: '1 1 520px',
      minWidth: 0,
      position: 'relative',
      display: 'flex',
      justifyContent: 'center'
    },
    ventana: {
      width: '100%',
      maxWidth: 560,
      borderRadius: 18,
      overflow: 'hidden',
      background: claro ? '#ffffff' : C.panel,
      border: `1px solid ${claro ? '#e8ebf5' : C.borde}`,
      boxShadow: claro
        ? '0 30px 70px rgba(30, 41, 90, .16)'
        : '0 30px 70px rgba(0, 0, 0, .5)'
    },
    barraVentana: {
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '15px 18px',
      borderBottom: `1px solid ${claro ? '#f0f2f8' : C.borde}`
    },
    punto: (color) => ({ width: 11, height: 11, borderRadius: '50%', background: color, flexShrink: 0 }),
    urlFalsa: {
      flex: 1, height: 9, borderRadius: 5, marginLeft: 12,
      background: claro ? '#eef1f8' : C.panelAlto
    },

    rejilla: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: 8,
      padding: '30px 24px 26px'
    },
    baldosa: { textAlign: 'center' },
    baldosaIcono: (color) => ({
      width: 56, height: 56, borderRadius: 15, margin: '0 auto 11px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 25,
      background: `linear-gradient(145deg, ${color} 0%, ${color}cc 100%)`,
      boxShadow: `0 8px 18px ${color}40`
    }),
    baldosaNombre: { fontSize: 12.5, color: C.suave, fontWeight: 500 },

    pie: {
      margin: '0 20px 20px',
      borderRadius: 12,
      background: claro ? '#1e293b' : '#0b1220',
      display: 'flex', alignItems: 'center', gap: 11,
      padding: '15px 17px'
    },
    pieIcono: {
      width: 26, height: 26, borderRadius: 7, flexShrink: 0,
      background: 'linear-gradient(140deg, #6366f1 0%, #4f46e5 100%)'
    },
    pieBarra: (ancho) => ({ height: 8, borderRadius: 4, background: '#334155', width: ancho }),

    pastillaSegura: {
      position: 'absolute', top: -14, right: 0,
      display: 'inline-flex', alignItems: 'center', gap: 9,
      padding: '11px 20px', borderRadius: 999,
      fontSize: 13.5, fontWeight: 500,
      background: claro ? '#ffffff' : C.panelAlto,
      color: C.texto,
      border: `1px solid ${claro ? '#e8ebf5' : C.borde}`,
      boxShadow: claro ? '0 10px 28px rgba(30, 41, 90, .14)' : '0 10px 28px rgba(0,0,0,.45)'
    },
    puntoVerde: { width: 9, height: 9, borderRadius: '50%', background: '#22c55e', flexShrink: 0 },

    aviso: {
      position: 'absolute', bottom: 8, left: 0,
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '13px 20px 13px 14px', borderRadius: 14,
      background: claro ? '#ffffff' : C.panelAlto,
      border: `1px solid ${claro ? '#e8ebf5' : C.borde}`,
      boxShadow: claro ? '0 14px 34px rgba(30, 41, 90, .16)' : '0 14px 34px rgba(0,0,0,.5)',
      maxWidth: 320
    },
    avisoIcono: {
      width: 34, height: 34, borderRadius: 9, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 16, background: 'linear-gradient(145deg, #059669 0%, #047857 100%)'
    },
    avisoTitulo: { fontSize: 13.5, fontWeight: 600, lineHeight: 1.35 },
    avisoTexto: { fontSize: 12.5, color: C.suave, lineHeight: 1.35 },

    creditos: {
      textAlign: 'center',
      fontSize: 13,
      color: C.tenue,
      padding: '0 24px 28px'
    },

    error: {
      marginTop: 22,
      padding: '12px 15px',
      borderRadius: 10,
      fontSize: 13.5,
      lineHeight: 1.55,
      maxWidth: 470,
      background: claro ? '#fef2f2' : '#3f1d1d',
      color: claro ? '#991b1b' : '#fca5a5',
      border: `1px solid ${claro ? '#fecaca' : '#7f1d1d'}`
    }
  };
};

/**
 * Reglas que los estilos en línea no pueden expresar: el apilado en pantallas
 * estrechas y el realce del botón al pasar por encima.
 */
const CSS = `
.bienvenida-boton { transition: transform .15s ease, box-shadow .15s ease; }
.bienvenida-boton:hover { transform: translateY(-1px); }
.bienvenida-boton:active { transform: translateY(0); }

@media (max-width: 1080px) {
  .bienvenida-cuerpo { flex-direction: column; gap: 56px; padding: 44px 28px; }
  .bienvenida-izquierda { max-width: 100%; text-align: center; }
  .bienvenida-izquierda .bienvenida-marca { justify-content: center; }
  .bienvenida-izquierda p { margin-left: auto; margin-right: auto; }
  .bienvenida-derecha { width: 100%; }
}

/* La maqueta flotante no cabe bien en un móvil: se deja solo el mensaje. */
@media (max-width: 720px) {
  .bienvenida-derecha { display: none; }
  .bienvenida-cuerpo { padding: 40px 22px; }
}
`;

const LogoMicrosoft = () => (
  <svg width="19" height="19" viewBox="0 0 21 21" aria-hidden="true" style={{ flexShrink: 0 }}>
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

const Bienvenida = ({ onLogin, entrando = false, error = null }) => {
  const { C } = useTema();
  const S = useMemo(() => estilos(C), [C]);

  return (
    <div style={S.raiz}>
      <style>{CSS}</style>

      <div className="bienvenida-cuerpo" style={S.cuerpo}>
        <div className="bienvenida-izquierda" style={S.izquierda}>
          <div className="bienvenida-marca" style={S.marca}>
            <span style={S.marcaIcono}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="3" y="3" width="7" height="7" rx="2" fill="#fff" />
                <rect x="14" y="3" width="7" height="7" rx="2" fill="#fff" opacity=".75" />
                <rect x="3" y="14" width="7" height="7" rx="2" fill="#fff" opacity=".75" />
                <rect x="14" y="14" width="7" height="7" rx="2" fill="#fff" opacity=".5" />
              </svg>
            </span>
            <span>
              <div style={S.marcaNombre}>Minutas BOT</div>
              <div style={S.marcaSub}>Fábrica de Software</div>
            </span>
          </div>

          <h1 style={S.titulo}>
            De la reunión al acta,<br />
            <span style={S.acento}>sin reescribir nada.</span>
          </h1>

          <p style={S.entradilla}>
            Conecta tu cuenta de Microsoft 365 y genera las minutas de tus reuniones de Teams
            sobre tu propio formato en Word, con el diseño intacto.
          </p>

          <button
            className="bienvenida-boton"
            style={S.boton}
            onClick={onLogin}
            disabled={entrando}
          >
            <LogoMicrosoft />
            {entrando ? 'Abriendo sesión…' : 'Iniciar sesión con Microsoft'}
          </button>

          {error && <div style={S.error} role="alert">{error}</div>}

        </div>

        {/* Decorativa: enseña lo que la app hace, no es una interfaz real. */}
        <div className="bienvenida-derecha" style={S.derecha} aria-hidden="true">
          <div style={S.ventana}>
            <div style={S.barraVentana}>
              <span style={S.punto('#ff5f57')} />
              <span style={S.punto('#febc2e')} />
              <span style={S.punto('#28c840')} />
              <span style={S.urlFalsa} />
            </div>

            <div style={S.rejilla}>
              {HERRAMIENTAS.map(h => (
                <div key={h.nombre} style={S.baldosa}>
                  <div style={S.baldosaIcono(h.color)}>{h.icono}</div>
                  <div style={S.baldosaNombre}>{h.nombre}</div>
                </div>
              ))}
            </div>

            <div style={S.pie}>
              <span style={S.pieIcono} />
              <span style={S.pieBarra(120)} />
              <span style={{ flex: 1 }} />
              <span style={S.pieBarra(56)} />
            </div>
          </div>

          <div style={S.pastillaSegura}>
            <span style={S.puntoVerde} />
            Sesión segura · Microsoft 365
          </div>

          <div style={S.aviso}>
            <span style={S.avisoIcono}>📄</span>
            <span>
              <div style={S.avisoTitulo}>Acta de Reunión.docx</div>
              <div style={S.avisoTexto}>12 campos rellenados · formato intacto</div>
            </span>
          </div>
        </div>
      </div>

      <div style={S.creditos}>
        Blue Ocean Technologies, S.A. de C.V. · Minutas BOT
      </div>
    </div>
  );
};

export default Bienvenida;
